import * as React from "react";
import { useEffect, useState, useRef, useCallback } from 'react';

import {
  Box,
  Stack,
  Heading,
  Text,
  Input,
  Image,
  Button,
  Container,
  Flex,
  SimpleGrid,
  HStack,
  Link,
  Select,
  ButtonGroup,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  FormControl,
  FormLabel,
  DrawerFooter,
  useDisclosure,
  Spacer,
  Skeleton
} from "@chakra-ui/react";
import { PageSlideFade } from "../components/page-transitions";
import { apiHost } from "utils/Cons/Constants";
import axios from "axios";
import { Helmet } from "react-helmet";
import parser from "html-react-parser";
import "../style/style.css";
import { MotionBox } from "../components/motion";
import Slider from "react-slick";
import { Field, Formik } from "formik";

interface Post {
  id: number;
  title: string;
  source_link?: string;
  main_img?: string;
  month?: string;
  day?: string;
  summary?: string;
  slug?: string;
  author_name?: string;
}

interface PageData {
  fields: {
    content_blocks?: any[];
  };
  meta_title?: string;
  meta_description?: string;
  header_image_url?: string;
  title?: string;
  entry?: {
    content?: string;
    subtitle?: string;
  };
}

interface Variables {
  default_post_img?: string;
  [key: string]: any;
}

interface FilterFormValues {
  title: string;
  category: any;
}

// Configurable pagination settings
const POSTS_PER_PAGE = 48;

const Posts = () => {
  const [initialLoading, setInitialLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [posts, setPosts] = useState<Post[]>([]);
  const [featuredPosts, setFeaturedPosts] = useState<Post[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [page, setPage] = useState<PageData>({ fields: {} });
  const [variables, setVariables] = useState<Variables>({});
  const [grid, setGrid] = useState(true);
  const [searchValues, setSearchValues] = useState<any>({});
  const [activeCategory, setActiveCategory] = useState('all'); // Track which category button is active

  // Pagination state
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: POSTS_PER_PAGE,
    total: 0,
    last_page: 1,
    from: 0,
    to: 0,
    has_more_pages: false
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadAttempts, setLoadAttempts] = useState(0);
  const [isScrollLoading, setIsScrollLoading] = useState(false);
  const [lastScrollTime, setLastScrollTime] = useState(0);
  const currentPageRef = useRef(1);


  const breakpointColumnsObj = {
    default: 6,
    1300: 4,
    900: 1
  };

  const breakpointColumnsListObj = {
    default: 3
  };

  // drawers
  const {
    isOpen: isFilterOpen,
    onOpen: onFilterOpen,
    onClose: onFilterClose
  } = useDisclosure()

  const settingsSlickEvents = {
    dots: false,
    arrows: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    adaptiveHeight: true,
    autoplay: true,
    autoplaySpeed: 8000,
    responsive: [
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 2,
        }
      },
      {
        breakpoint: 900,
        settings: {
          slidesToShow: 1,
        }
      }
    ]
  };

  const getFeaturedPosts = (limit = 10, featured = 0) => {
    const searchParams = {
      limit: limit,
      featured: featured
    };
    console.log('Fetching posts with params:', searchParams);
    axios.get(`${apiHost}/news/list`, { params: searchParams })
      .then(res => {
        if (res.status === 200) {
          const responseData = res.data.data;
          setFeaturedPosts(responseData);
        }
        return res;
      })
      .catch(err => {
        console.log('Error fetching posts:', err);
      });
    return true;
  };

  const getExperience = (values = null, append = false) => {
    let data = [];

    const page = append ? currentPageRef.current + 1 : 1;
    const limit = POSTS_PER_PAGE;

    const searchParams = {
      ...values,
      page: page,
      limit: limit
    };

    console.log('Fetching posts with params:', searchParams);

    axios.get(`${apiHost}/news/search`, { params: searchParams })
      .then(res => {
        if (res.status === 200) {
          const responseData = res.data;
          data = responseData.data || responseData; // Handle both paginated and non-paginated responses
          const paginationData = responseData.pagination || {};

          console.log('Posts response data length:', data.length);
          console.log('Pagination data:', paginationData);

          if (append) {
            setPosts(prevPosts => [...prevPosts, ...data]);
            setLoadAttempts(prev => prev + 1);
            // Update current page ref
            currentPageRef.current = page;
            // Update pagination state
            setPagination(prevPagination => ({
              ...prevPagination,
              current_page: page,
              has_more_pages: paginationData.has_more_pages !== false,
              ...(paginationData.total && { total: paginationData.total }),
              ...(paginationData.last_page && { last_page: paginationData.last_page }),
              ...(paginationData.from && { from: paginationData.from }),
              ...(paginationData.to && { to: paginationData.to })
            }));
            setTimeout(() => {
              setLoadingMore(false);
              setIsScrollLoading(false);
            }, 500);
          } else {
            setPosts(data);
            setLoadAttempts(0);
            // Update pagination state with API response for new search
            setPagination(paginationData);
            currentPageRef.current = 1;
            setLoadingMore(false);
          }

          setInitialLoading(false);
        }
        return res;
      })
      .catch(err => {
        console.log('Error fetching posts:', err);
        setInitialLoading(false);
        setLoadingMore(false);
      });

    return data;
  };

  const getCategories = () => {
    let data = [];
    setCategoriesLoading(true);
    console.log('Fetching categories from:', `${apiHost}/news/categories/list`);
    axios.get(`${apiHost}/news/categories/list`)
      .then(res => {
        data = res.data;
        console.log('Categories response:', data);
        console.log('Categories response status:', res.status);
        if (res.status === 200) {
          console.log('Setting categories:', data);
          setCategories(data);
          setCategoriesLoading(false);
        }
        return res;
      })
      .catch(err => {
        console.log('Error fetching categories:', err);
        setCategoriesLoading(false);
        // Set empty array on error
        setCategories([]);
      });

    return data;
  };

  const getPage = () => {
    let data = [];
    axios.get(`${apiHost}/page/get/news`)
      .then(res => {
        data = res.data;
        if (res.status === 200) {
          setPage(data as unknown as PageData);
        }
        return res;
      })
      .catch(err => console.log(err));

    return data;
  };

  const getVariables = () => {
    let data = {} as Variables;
    axios.get(`${apiHost}/variables/get/global`)
      .then(res => {
        data = res.data as Variables;
        if (res.status === 200) {
          console.log(data);
          setVariables(data as Variables);
        }
        return res;
      })
      .catch(err => console.log(err));

    return data;
  };

  const loadMorePosts = () => {
    if (!loadingMore && canLoadMore()) {
      setLoadingMore(true);
      getExperience(searchValues, true);
    }
  };

  // Check if we can load more posts
  const canLoadMore = useCallback(() => {
    const hasFullPage = posts.length % POSTS_PER_PAGE === 0;
    const isLastPage = posts.length < POSTS_PER_PAGE;
    return (
      (pagination.has_more_pages === true) ||
      (pagination.current_page < pagination.last_page && pagination.last_page > 0) ||
      (hasFullPage && !isLastPage) ||
      (posts.length > 0 && pagination.total > posts.length && pagination.total > 0) ||
      (posts.length >= POSTS_PER_PAGE && (!pagination.total || pagination.total === 0)) ||
      (posts.length >= POSTS_PER_PAGE && loadAttempts < 5)
    );
  }, [pagination, posts.length, loadAttempts]);

  // Scroll-based loading function
  const handleScrollLoad = useCallback(() => {
    const now = Date.now();
    // Throttle scroll events to prevent excessive API calls (300ms minimum between calls)
    if (now - lastScrollTime < 300) return;

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;

    // Check if user has scrolled at all (any scroll movement triggers loading)
    const hasScrolled = scrollTop > 0;
    
    // Also check if user is near the bottom (within 500px for more aggressive loading)
    const isNearBottom = scrollTop + windowHeight >= documentHeight - 500;

    // Trigger loading on any scroll if we can load more and aren't already loading
    if ((hasScrolled || isNearBottom) && canLoadMore() && !loadingMore && !isScrollLoading) {
      setLastScrollTime(now);
      setIsScrollLoading(true);
      setLoadingMore(true);
      getExperience(searchValues, true);
    }
  }, [lastScrollTime, canLoadMore, loadingMore, isScrollLoading, searchValues]);

  const sortClick = function (order: string) {
    const values = { ...searchValues, order };
    console.log('Sorting with order:', order, 'and values:', values);
    setSearchValues(values);
    getExperience(values);
  }

  const onGridList = function (breakpointColumns) {
    if (breakpointColumns.default === 3) {
      setGrid(false);
    } else {
      setGrid(true);
    }
  }

  const getCategory = (id) => {
    // Set active category to the selected category ID
    setActiveCategory(id.toString());
    // Reset pagination to first page
    currentPageRef.current = 1;
    let values = {
      categories: [id]
    };
    console.log('Filtering by category:', id, 'with values:', values);
    setSearchValues(values);
    getExperience(values);
    return false;
  }

  const handleShowAllPosts = () => {
    // Set active category to 'all'
    setActiveCategory('all');
    // Reset pagination to first page
    currentPageRef.current = 1;
    // Create search values without categories
    const allPostsValues = {};
    // Update search values and make API request
    setSearchValues(allPostsValues);
    getExperience(allPostsValues);
  }

  useEffect(() => {
    window.scrollTo(0, 0);
    getPage();
    getFeaturedPosts(100, 1);
    getExperience();
    getCategories();
    getVariables();
    // Ensure we start with "All" category active
    setActiveCategory('all');
  }, []);

  // Add scroll event listener for automatic loading
  useEffect(() => {
    const handleScroll = () => {
      handleScrollLoad();
    };

    // Add scroll event listener
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScrollLoad]);



  return (
    <PageSlideFade>
      <Helmet>
        <title>{page.meta_title}</title>
        <meta name="description" content={page.meta_description} />
      </Helmet>
      <Box className="home-header-container" backgroundImage={`url(${page.header_image_url})`} backgroundPosition="center center" backgroundSize="cover" minHeight="800px">
              <Container maxW={'5xl'}>
                  <Stack
                      textAlign={'center'}
                      align={'center'}
                      spacing={{ base: 8, md: 10 }}
                      py={{ base: 20, md: 28 }}>
                      <MotionBox
                          w="100%"
                          opacity="0"
                          initial={{
                              translateY: 180
                          }}
                          animate={{
                              translateY: 0,
                              opacity: 1,
                              transition: {
                                  delay: 0.4,
                                  duration: 0.5
                              }
                          }}
                      >
                          <Heading
                              as={'h1'}
                              fontFamily="american"
                              fontWeight={600}
                              fontSize={{ base: '3xl', sm: '4xl', md: '6xl' }}
                              color="white"
                              lineHeight={'100%'}
                              textShadow="2px 2px 8px rgba(0,0,0,0.5)"
                              mt={{ base: 20, md: 0 }}>
                              {page.title} {' '}
                              <Text as={'div'} fontFamily="brush" fontWeight={500}
                                  fontSize={{ base: '6rem', md: '13rem' }} color="white" textShadow="2px 2px 8px rgba(0,0,0,0.5)" mt="6rem">
                                  Bellefonte
                              </Text>
                          </Heading>
                      </MotionBox>
                  </Stack>

              </Container>
          </Box>
      {page.entry && page.entry.subtitle ? (
      <Box minHeight="25vh" bg="#E6DFD3">
        <Container maxW={'6xl'}>
          <Box pt={10} pb={10}>
              <Text fontSize="5xl" color="white" textAlign="left">
                {parser(page.entry.subtitle)}
              </Text>
          </Box>
        </Container>
      </Box>
      ) : null}
      {page.entry && page.entry.content ? (
        <Box bg="white">
          <Container maxW={'6xl'}>
            <Box className="content-block" pt={10} pb={10}>
              <Text fontSize="3xl" textAlign="left">
                {parser(page.entry.content)}
              </Text>
            </Box>
          </Container>
        </Box>
      ) : null}

      {featuredPosts ? (
        <Box bg="white">
          <Box>
            <Box pl={20} pr={20} pb={10}>
              <Slider {...settingsSlickEvents} className="events-slider">
                {featuredPosts?.map((post, index) => (
                  <div>
                    <Link href={post.source_link ? post.source_link : `/posts/${post.slug}`} target={post.source_link ? "_blank" : "_self"} >
                      <Box backgroundImage={`url(${post.main_img})`} backgroundPosition="center center" backgroundSize="cover" minHeight="450px" position="relative" margin="10px">
                        <Heading textAlign="left" position="absolute" top="10px" left="20px" color="white" textShadow="0px 0px 5px #000" lineHeight="1em">{post.title}</Heading>
                      </Box>
                    </Link>
                  </div>
                ))}
              </Slider>
            </Box>
          </Box>
        </Box>
      ) : null}
      <Box bg="white">
        <Container maxW={'8xl'}>
          <Box pb={10}>
            <Button
              onClick={() => handleShowAllPosts()}
              fontSize={{ base: "10px", md: "12px" }}
              m="4px"
              borderTopStartRadius={5}
              borderBottomStartRadius={5}
              borderTopEndRadius={5}
              borderBottomEndRadius={5}
              bg={activeCategory === 'all' ? "#fbeec8" : undefined}
              color={activeCategory === 'all' ? "black" : undefined}
              _hover={{
                bg: activeCategory === 'all' ? "#143b49" : undefined,
                color: activeCategory === 'all' ? "white" : undefined
              }}
            >
              All
            </Button>
            {categories?.map((cat, index) => (
              <Button
                onClick={() => getCategory(cat.id)}
                key={index}
                fontSize={{ base: "10px", md: "12px" }}
                m="4px"
                borderTopStartRadius={5}
                borderBottomStartRadius={5}
                borderTopEndRadius={5}
                borderBottomEndRadius={5}
                bg={activeCategory === cat.id.toString() ? "#fbeec8" : undefined}
                color={activeCategory === cat.id.toString() ? "black" : undefined}
                _hover={{
                  bg: activeCategory === cat.id.toString() ? "#143b49" : undefined,
                  color: activeCategory === cat.id.toString() ? "white" : undefined
                }}
              >
                {cat.name}
              </Button>
            ))}
          </Box>
        </Container>
      </Box>

      <Box bg="white" pt={5}>
        <Box>
          <SimpleGrid columns={3}>
            <Box textAlign="left">
              <Button onClick={() => onFilterOpen()} p={5} borderLeftRadius={0}>Filter</Button>
            </Box>
            <Box display={{ base: "none", md: "block" }}>
              <ButtonGroup spacing='2'>
                <Button
                  onClick={() => onGridList(breakpointColumnsObj)}
                  p={5}
                  borderTopEndRadius={0}
                  borderBottomEndRadius={0}
                  bg={grid ? "#D51F0C" : "gray.200"}
                  color={grid ? "white" : "black"}
                  _hover={{
                    bg: grid ? "#143b49" : "gray.300"
                  }}
                >
                  Grid
                </Button>
                <Button
                  onClick={() => onGridList(breakpointColumnsListObj)}
                  p={5}
                  borderTopStartRadius={0}
                  borderBottomStartRadius={0}
                  bg={!grid ? "#D51F0C" : "gray.200"}
                  color={!grid ? "white" : "black"}
                  _hover={{
                    bg: !grid ? "#143b49" : "gray.300"
                  }}
                >
                  List
                </Button>
              </ButtonGroup>
            </Box>
            <Box display="flex" flexDirection="row" alignItems="right" >
              <HStack marginLeft="auto" mb={5} mr={{ base: 0, md: 5 }}>
                <Spacer />
                <Text w="100px" textAlign="right">Sort By</Text>
                <Select
                  onChange={(e) => {
                    sortClick(e.target.value);
                  }} w={{ base: "120px", md: "200px" }}>
                  <option value="latest">Latest</option>
                  <option value="oldest">Oldest</option>
                </Select>
              </HStack>
            </Box>
          </SimpleGrid>
        </Box>

        {initialLoading && (
          <Box p={5}>
            <Skeleton height="80px" />
            <Text mt="-50px" mb="50px" fontWeight="bold" textAlign="center">
              Loading Happy Valley Posts ...
            </Text>
          </Box>
        )}

        <SimpleGrid columns={{ base: 1, md: 1 }} spacing={10}>
          <Box p={5}>
            {!initialLoading && (
              <SimpleGrid
                columns={grid ? { base: 1, sm: 2, lg: 3, xl: 4, '2xl': 4 } : { base: 1, md: 2, lg: 3, xl: 4 }}
                spacing={5}
                className="posts-grid"
              >
                {posts?.map((solution, index) => (
                  <Box
                    key={`${solution.id}-${solution.slug}-${index}`}
                    w={'100%'}
                    boxShadow={'0 0 10px 2px #ccc'}
                    rounded={10}
                    bg="#FFF"
                    _hover={{
                      bg: '#F7E6CE'
                    }}
                    h="100%"
                    display="flex"
                    flexDirection="column"
                    overflow="hidden"
                  >
                    <Box position="relative" flex="1">
                      <Link href={solution.source_link ? solution.source_link : `/posts/${solution.slug}`} target={solution.source_link ? "_blank" : "_self"}>
                        {(solution.main_img || variables.default_post_img) && grid && (
                          <Image
                            src={`${solution.main_img || variables.default_post_img}`}
                            w={'100%'}
                            borderTopRadius={10}
                          />
                        )}
                        <Box p={4}>
                          {!grid && (
                            <Flex align="start" gap={4} minHeight="120px">
                              {solution.main_img ? (
                                <Image
                                  src={`${solution.main_img}`}
                                  w="80px"
                                  h="80px"
                                  objectFit="cover"
                                  borderRadius="md"
                                  flexShrink={0}
                                  alt={'post image'}
                                />
                              ) : (
                                  variables.default_post_img ? (
                                    <Image
                                      src={`${variables.default_post_img}`}
                                      w="80px"
                                      h="80px"
                                      objectFit="cover"
                                      borderRadius="md"
                                      flexShrink={0}
                                      alt={'post image'} />
                                  ) : null
                                )}
                              <Box 
                                flex="1" 
                                display="flex" 
                                flexDirection="column" 
                                h="100%"
                                minHeight="180px"
                              >
                                <Box flex="1">
                                  <Heading fontSize="1.45rem" fontFamily="Roboto" fontWeight="800" textAlign="left" lineHeight="115%" color="#143b49" noOfLines={2}
                                    overflow="hidden" pb={0} mb={2}
                                    textOverflow="ellipsis">{solution.title}</Heading>
                                  {solution.summary && (
                                    <Text fontSize="1rem" textAlign="left" mb={2} lineHeight="100%"
                                    noOfLines={4}
                                    overflow="hidden"
                                    textOverflow="ellipsis">{solution.summary}</Text>
                                  )}
                                </Box>
                                <Button
                                  mt="auto"
                                  w="100%"
                                  borderTopRadius={10}
                                  borderBottomRadius={10}
                                  as={Link}
                                  href={solution.source_link ? solution.source_link : `/posts/${solution.slug}`}
                                  target={solution.source_link ? "_blank" : "_self"}
                                  color="#fff"
                                  bg="#D51F0C"
                                  _hover={{
                                    bg: "#143b49",
                                    color: "white"
                                  }}
                                >
                                  Read Full Article
                                </Button>
                              </Box>
                            </Flex>
                          )}
                          {grid && (
                            <Box
                              display="flex"
                              flexDirection="column"
                              h="100%"
                              minHeight="200px"
                            >
                              <Box flex="1">
                                <Heading fontSize="1.45rem" fontFamily="Roboto" fontWeight="800" textAlign="left" lineHeight="125%" color="#143b49" mb={2} pb={0} noOfLines={2} overflow="hidden" textOverflow="ellipsis">{solution.title}</Heading>
                                {solution.summary && (
                                  <Text fontSize="1rem"
                                    color="#143b49"
                                    textAlign="left"
                                    mb={2}
                                    lineHeight="100%"
                                    noOfLines={4}
                                    overflow="hidden"
                                    textOverflow="ellipsis">{solution.summary}</Text>
                                )}
                              </Box>
                              <Button
                                mt="auto"
                                w="100%"
                                borderTopRadius={10}
                                borderBottomRadius={10}
                                as={Link}
                                href={solution.source_link ? solution.source_link : `/posts/${solution.slug}`}
                                target={solution.source_link ? "_blank" : "_self"}
                                color="#fff"
                                bg="#D51F0C"
                                _hover={{
                                  bg: "#143b49",
                                  color: "white"
                                }}
                              >
                                Read Full Article
                              </Button>
                            </Box>
                          )}
                        </Box>
                      </Link>
                    </Box>
                  </Box>
                ))
                }
              </SimpleGrid>
            )}

            {/* Loading indicator for pagination */}
            {loadingMore && (
              <Box textAlign="center" mt={4} mb={4}>
                <Text fontSize="sm" color="gray.500" mb={2}>
                  Loading more posts...
                </Text>
                <Skeleton height="4px" width="200px" mx="auto" />
              </Box>
            )}

            {/* Load More Button - Fallback when scroll loading doesn't work */}
            {!initialLoading && !loadingMore && canLoadMore() && !(pagination.total && posts.length >= pagination.total) && (
              <Box textAlign="center" mt={8}>
                <Text fontSize="sm" color="gray.500" mb={4}>
                  {isScrollLoading ? "Loading more posts automatically..." : "Scroll down to load more posts automatically, or click below:"}
                </Text>
                <Button
                  onClick={loadMorePosts}
                  isLoading={loadingMore}
                  loadingText="Loading more posts..."
                  bg="#D51F0C"
                  color="white"
                  size="lg"
                  px={8}
                  borderTopRadius="full"
                  borderBottomRadius="full"
                  _hover={{
                    bg: "#143b49"
                  }}
                >
                  Load More Posts
                </Button>
              </Box>
            )}

            {/* Show pagination info */}
            {!initialLoading && posts.length > 0 && pagination.total && pagination.total > 0 && (
              <Box textAlign="center" mt={4}>
                <Text color="gray.600" fontSize="sm">
                  Showing {posts.length} of {pagination.total} posts
                </Text>
              </Box>
            )}
          </Box>
        </SimpleGrid>
      </Box>
      <Drawer
        isOpen={isFilterOpen}
        size="md"
        placement="left"
        closeOnOverlayClick={true}
        onClose={onFilterClose}
      >
        <DrawerOverlay />

        <Formik
          enableReinitialize
          initialValues={{ title: "", category: null } as FilterFormValues}
          onReset={(values, actions) => {
            console.log('Resetting filter form');
            setActiveCategory('all');
            setSearchValues({});
            getExperience();
            onFilterClose();
          }}
          onSubmit={(values: FilterFormValues, actions) => {
            const searchParams: any = {};

            if (values.title && values.title.trim()) {
              searchParams.title = values.title.trim();
            }

            if (values.category) {
              searchParams.categories = [values.category];
              setActiveCategory(values.category.toString());
            } else {
              setActiveCategory('all');
            }

            console.log('Filter submission with params:', searchParams);
            setSearchValues(searchParams);
            getExperience(searchParams);
            onFilterClose();
            actions.setSubmitting(false);
          }}
        >
          {({ values, handleChange, handleSubmit, handleReset, isSubmitting }) => {
            console.log('Form values:', values);
            return (
              <form id="searchForm" autoComplete="off" onSubmit={handleSubmit} onReset={handleReset} onChange={handleChange} style={{ width: "100%" }}>
                <DrawerContent>
                  <DrawerCloseButton />
                  <DrawerHeader borderBottomWidth="1px">
                    <Heading pb={0}>Filter</Heading>
                  </DrawerHeader>

                  <DrawerBody>

                    <Field
                      name="category"
                      width={"100%"}
                    >
                      {({ field }) => (
                                                <FormControl>
                          <FormLabel>Category</FormLabel>
                          {categoriesLoading ? (
                            <Select placeholder="Loading categories..." disabled></Select>
                          ) : (
                            <Select {...field}
                              id="category"
                              borderRadius="15px"
                              fontSize="sm"
                              type="text"
                              placeholder="All"
                              size="lg"
                              value={values.category || ""}
                              onChange={handleChange}>
                                {categories && categories.length > 0 ? categories.map((cat, index) => (
                                  <option key={index} value={cat.id}>{cat.name}</option>
                                )) : (
                                    <option value="" disabled>No categories available</option>
                                  )}
                              </Select>
                          )}
                        </FormControl>
                      )}
                    </Field>

                    <Field
                      name="title"
                      width={"100%"}
                    >
                      {({ field }) => (
                        <FormControl>
                          <FormLabel mt={2}>Post Title</FormLabel>
                          <Input
                            {...field}
                            id="title"
                            borderRadius="15px"
                            fontSize="sm"
                            type="text"
                            placeholder='Search posts...'
                            size="lg"
                            value={values.title}
                            onChange={handleChange} />
                        </FormControl>
                      )}
                    </Field>

                    <Box height="70vh">

                    </Box>

                  </DrawerBody>

                  <DrawerFooter borderTopWidth="1px" justifyContent="flex-end">
                    <Button mr={3} onClick={onFilterClose}>
                      Close
                    </Button>
                    <Button mr={3} type="reset">Reset</Button>
                    <Button mr={3} type="submit" isLoading={isSubmitting}>Search</Button>
                  </DrawerFooter>
                </DrawerContent>
              </form>
            );
          }}
        </Formik>
      </Drawer>
    </PageSlideFade>
  );
};

export default Posts;
